/* @moldura padrao */
/* =============================================================================
   AGENDA / INSTALAÇÕES — a tela oficial
   =============================================================================
   Limpa em 25/08/2026, e desde então é ESTA que responde em `/agenda/instalacoes`.
   A versão anterior ficou guardada em `instalacoes-antiga.tsx` (rota
   `/agenda/instalacoes-antiga`) só para conferência, e sai quando o redesenho fechar.

   O QUE MUDOU NA LIMPEZA — e é só isto, nada de aparência:

   | Antes                                        | Agora                                       |
   |----------------------------------------------|---------------------------------------------|
   | 114 linhas de CSS numa string aqui dentro   | `estilos/_cartao-agendamento.scss` + a folha da tela |
   | `card-barra-blue` / `evento-barra-blue`      | `casaa-cartao-agendamento--tipo-instalacao` |
   | a lista de 9 cores, duplicada nas duas telas | UMA lista, e o nome carrega a peça          |
   | `var(--bs-blue)`                             | `var(--casaa-tipo-instalacao)`              |

   No modo CLARO nada mudou: `--casaa-azul` tem exatamente o tom que estava aqui.
   O que muda é o ESCURO, onde as cores clareiam em vez de queimar a vista.

   E some uma classe inteira de bug: uma crase dentro daquela string de CSS
   deixava a tela em branco sem o TypeScript acusar — aconteceu em 09/08/2026
   (memória `teste-1-css-local-crase`). Em `.scss` a crase é caractere comum.
   ============================================================================= */
/**
 * instalacoes.tsx (agenda) — A AGENDA DO INSTALADOR. Uma semana, e só.
 *
 * TELA PRÓPRIA, e não a de Agendamentos com permissões (decisão dele,
 * 09/08/2026). O trabalho é outro: a Suany MARCA — precisa de "OS a agendar",
 * arrastar, editar, imprimir. O instalador só precisa saber **onde ir e o que
 * fazer**, com o celular na mão, dentro da caminhonete. Uma tela que serve aos
 * dois acaba servindo mal a um.
 *
 * DOIS MODOS, e ele escolhe: CALENDÁRIO (o FullCalendar da tela de
 * Agendamentos, limitado a uma semana) e LISTA (um bloco por dia). A lista
 * nasceu primeiro por caber melhor em tela estreita; o calendário entrou porque
 * o Vittorio quis ver a semana como semana — "está horroroso", disse ele da
 * lista sozinha (09/08/2026). O calendário é o padrão; a lista continua a um
 * toque, e é ela que se lê bem no celular apertado.
 *
 * SÓ O QUE A SUANY LIBEROU. Ele não deve saber com muita antecedência o que
 * vai fazer — regra do Vittorio. Até 09/08/2026 isso era uma CONSTANTE aqui no
 * código (`PROXIMA_SEMANA_LIBERADA = false`), o que dava o horizonte certo pela
 * razão errada: quem decide é quem agenda, não o front do instalador. Agora a
 * liberação é dado (migration 047), marcada por ela na tela de Agendamentos em
 * três níveis — card, dia ou semana.
 *
 * ESTA TELA NÃO FILTRA NADA. O filtro é do BACKEND: ela pede
 * `GET /visitas?visao=instalador`, e para o perfil `instalador` o filtro vale
 * de qualquer jeito. Aberta pela Suany ou pelo admin, mostra o mesmo que ele
 * veria — é assim que se confere a liberação sem trocar de login. Se um dia isto virar responsa da
 * tela, a regra passa a depender de quem consome — que é justamente o que se
 * quis evitar.
 *
 * A PRÓXIMA SEMANA aparece quando ela libera alguma coisa nela: o botão deixa
 * de ser decorativo e passa a refletir o que existe do outro lado.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import ptBr from '@fullcalendar/core/locales/pt-br';
import { api, ApiError } from '@/lib/api';
import { nomeCurtoDaViagem, roteiroCompleto } from '@/lib/viagem';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/auth-context';


registerLocale('pt-BR', ptBR);

type Modo = 'calendario' | 'lista';

interface Visita {
  id: number;
  os_id: number | null;
  os_codigo: string | null;
  os_tipo: string | null;
  tipo: string;
  titulo: string | null;
  /** 'YYYY-MM-DD'. A hora vem à parte — não é um timestamp. */
  data: string;
  /** 'HH:MM', ou null quando o evento é de dia inteiro. */
  hora: string | null;
  /** Preenchido quando a ida faz parte de uma viagem. */
  viagem_id: number | null;
  dia_inteiro: boolean;
  status: string;
  pauta: string | null;
  cliente_nome: string | null;
  endereco_resumo: string | null;
  bairro_cidade: string | null;
  loja_codigo: string | null;
}

/** Cor da barra por tipo — as MESMAS da tela de Agendamentos, para o instalador
 *  reconhecer o card que a Suany montou. Token do Bootstrap, sem hex. */
const PAPEL_OS: Record<string, string> = {
  instalacao: 'tipo-instalacao',
  manutencao: 'tipo-manutencao',
  lavagem: 'tipo-lavagem',
};
const PAPEL_EVENTO: Record<string, string> = {
  entrega: 'evento-entrega',
  retirada: 'evento-retirada',
  servico: 'evento-servico',
  ausencia: 'evento-ausencia',
  reuniao: 'evento-reuniao',
  dia_sem_os: 'evento-dia-sem-os',
  lembrete: 'evento-lembrete',
};
const ABREV_OS: Record<string, string> = {
  instalacao: 'INS',
  manutencao: 'MAN',
  lavagem: 'LAV',
};

/** O PAPEL da visita — o que ela é, não de que cor ela é.
 *  Quem decide o tom (e o tom do modo escuro) é `estilos/_cores.scss`. */
function papelDo(v: Visita): string {
  if (v.os_id) return v.os_tipo ? (PAPEL_OS[v.os_tipo] ?? 'evento-ausencia') : 'evento-ausencia';
  return PAPEL_EVENTO[v.tipo] ?? 'evento-lembrete';
}

interface Viagem {
  id: number;
  loja_id?: number | null;
  /** Como ela se chama onde o espaço é pouco. Vazio = usa a sugestão (048). */
  nome_curto: string | null;
  destinos: string[];
  partida: string | null;
  chegada: string | null;
}

const ROTULO_TIPO: Record<string, string> = {
  instalacao: 'Instalação',
  manutencao: 'Manutenção',
  lavagem: 'Lavagem',
  entrega: 'Entrega',
  retirada: 'Retirada',
  servico: 'Serviço',
  reuniao: 'Reunião',
  lembrete: 'Lembrete',
};

/**
 * A segunda-feira da semana que interessa.
 *
 * NO DOMINGO, OLHA PARA A FRENTE. Domingo é folga, e quem abre o app nele quer
 * saber o que vem — não o que passou. Se o domingo pertencesse à semana que
 * termina (a convenção ISO), o instalador abriria a tela e veria os serviços já
 * feitos, com "nada marcado" à vista para amanhã. Descoberto testando num
 * domingo, 09/08/2026.
 *
 * Consequência aceita: trabalho marcado NO domingo não aparece nesse mesmo dia.
 * É raro, e a alternativa (mostrar a semana morta) atrapalha todo domingo.
 */
function segundaDa(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay();
  if (dow === 0) {
    x.setDate(x.getDate() + 1); // domingo → a segunda de amanhã
    return x;
  }
  x.setDate(x.getDate() - (dow - 1));
  return x;
}

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

/**
 * A página de Instalações de uma loja: `/agenda/instalacoes/loja/tr`.
 * Sem loja no caminho vai para a primeira loja da pessoa. O MENU é o único
 * lugar onde se escolhe a loja — a página não tem botão de loja nem "Todas"
 * (decisão dele, 29/10/2026). O instalador de uma loja só cai direto na dela.
 */
export const caminhoInstalacoes = (codigo?: string) =>
  codigo ? `/agenda/instalacoes/loja/${codigo.toLowerCase()}` : '/agenda/instalacoes';

export function AgendaInstalacoesPage() {
  const { usuario } = useAuth();
  const { loja: lojaDoCaminho } = useParams();
  const lojasPagina = usuario?.lojas_instalacoes ?? [];
  const loja = (lojaDoCaminho ?? '').toUpperCase();
  const lojaAtual = lojasPagina.find((l) => l.codigo === loja);
  const filtroLoja = loja ? `&loja=${loja}` : '';
  const lojaIdAtual = lojaAtual?.id;
  const [visitas, setVisitas] = useState<Visita[]>([]);
  const [viagens, setViagens] = useState<Viagem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  /** A Suany já liberou alguma coisa na semana seguinte? É o que habilita o
   *  botão "Próxima semana" — antes isso era uma constante no código. */
  const [proximaLiberada, setProximaLiberada] = useState(false);
  /**
   * Calendário no computador, lista no celular — e ele troca à vontade.
   *
   * Não é preferência estética: `dayGridWeek` em 390px dá sete colunas de 48px,
   * onde "TESTE AGO — Beatriz Nogueira, Geribá" vira "TEST Geri". A lista usa a
   * largura toda e mostra endereço inteiro, que é o que o instalador foi
   * buscar. No computador é o contrário: a semana como semana se lê de uma vez,
   * e foi o que o Vittorio pediu.
   *
   * Escolha só no primeiro render: trocar de modo sozinho ao girar o telefone
   * seria pior que qualquer padrão.
   */
  // LISTA é o padrão, nas duas telas (decisão dele, 09/08/2026). No celular ela
  // é a única legível; no computador ele preferiu assim mesmo.
  const [modo, setModo] = useState<Modo>('lista');
  // Fim de semana: fica DESLIGADO por padrão porque sábado é exceção. O botão
  // aparece do lado do seletor de modo, como na tela de Agendamentos.
  const [fimDeSemana, setFimDeSemana] = useState(false);
  /** Card aberto. É só leitura — o instalador não edita nada aqui. */
  const [detalhe, setDetalhe] = useState<Visita | null>(null);
  // O mini-calendário nasce FECHADO: é consulta, não o assunto da tela. Quem
  // abre o app quer a lista do dia; o calendário é para quem foi se situar.
  const [miniAberto, setMiniAberto] = useState(false);
  /**
   * Dias recolhidos à mão. O que JÁ PASSOU nasce recolhido sozinho (ver
   * `recolhido()`), e este conjunto só guarda o que ele abriu ou fechou depois
   * — assim a regra automática continua valendo para o resto da semana sem
   * precisar mexer em nada.
   */
  const [alternados, setAlternados] = useState<Set<string>>(new Set());

  const alternarDia = (chave: string) =>
    setAlternados((s) => {
      const n = new Set(s);
      if (n.has(chave)) n.delete(chave);
      else n.add(chave);
      return n;
    });

  const hoje = useMemo(() => new Date(), []);
  /* Qual semana está na tela: 0 = esta. Só passa de 0 se a Suany tiver liberado
     algo na semana seguinte — o botão fica desabilitado até lá. */
  const [semanasAdiante, setSemanasAdiante] = useState(0);
  const segunda = useMemo(() => {
    const d = segundaDa(hoje);
    d.setDate(d.getDate() + semanasAdiante * 7);
    return d;
  }, [hoje, semanasAdiante]);
  // Domingo fecha a semana: eles às vezes trabalham sábado, e um card invisível
  // é pior que um dia vazio.
  const domingo = useMemo(() => {
    const d = new Date(segunda);
    d.setDate(d.getDate() + 6);
    return d;
  }, [segunda]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const seg2 = new Date(segunda);
      seg2.setDate(seg2.getDate() + 7);
      const dom2 = new Date(seg2);
      dom2.setDate(dom2.getDate() + 6);
      const [vs, vgs, proxima] = await Promise.all([
        api.get<Visita[]>(
          `/agenda/eventos?de=${iso(segunda)}&ate=${iso(domingo)}&visao=instalador${filtroLoja}`,
        ),
        // As viagens dão o CONTEXTO do dia: "isto é uma ida a Trancoso", não
        // três paradas soltas. O card sozinho não conta essa parte.
        api.get<Viagem[]>('/viagens?tipo=operacional').catch(() => []),
        // Sonda da semana seguinte: como o backend já filtra pelo perfil, vir
        // vazio significa "ela ainda não liberou nada lá" — é exatamente o que
        // o botão precisa saber, sem endpoint novo.
        api
          .get<Visita[]>(`/agenda/eventos?de=${iso(seg2)}&ate=${iso(dom2)}&visao=instalador${filtroLoja}`)
          .catch(() => []),
      ]);
      setVisitas(vs);
      // A viagem dá o contexto do dia; na página de uma loja, só as dela.
      setViagens(lojaIdAtual ? vgs.filter((v) => v.loja_id === lojaIdAtual) : vgs);
      setProximaLiberada(proxima.length > 0);
    } catch (err) {
      setErro(
        err instanceof ApiError ? err.message : 'Não foi possível carregar a semana.',
      );
    } finally {
      setCarregando(false);
    }
  }, [segunda, domingo, filtroLoja, lojaIdAtual]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /** Os sete dias, cada um com suas visitas em ordem de hora. */
  const dias = useMemo(() => {
    const out: { data: Date; chave: string; itens: Visita[] }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(segunda);
      d.setDate(d.getDate() + i);
      const chave = iso(d);
      out.push({
        data: d,
        chave,
        // Com hora primeiro, em ordem; sem hora depois. Hora é exceção na
        // operação, mas quando existe é ela que manda no roteiro do dia.
        itens: visitas
          .filter((v) => (v.data ?? '').slice(0, 10) === chave)
          .sort((a, b) => (a.hora ?? '99:99').localeCompare(b.hora ?? '99:99')),
      });
    }
    return out;
  }, [visitas, segunda]);

  /* O nome CURTO da viagem — o que cabe no chip do card, no celular, que é onde
     esta tela vive. O roteiro por extenso vai no `title` (nomeViagemCompleto). */
  const nomeViagem = useCallback(
    (id: number | null | undefined): string | null => {
      if (!id) return null;
      const vg = viagens.find((x) => x.id === id);
      if (!vg) return null;
      return nomeCurtoDaViagem(vg.nome_curto, vg.destinos) || `Viagem #${vg.id}`;
    },
    [viagens],
  );

  const nomeViagemCompleto = useCallback(
    (id: number | null | undefined): string | undefined => {
      if (!id) return undefined;
      const vg = viagens.find((x) => x.id === id);
      if (!vg) return undefined;
      return roteiroCompleto(vg.destinos) || undefined;
    },
    [viagens],
  );

  /** Dias com serviço e dias de viagem — o que o mini-calendário marca. */
  const diasMarcados = useMemo(() => {
    const servico = new Set<string>();
    const viagem = new Set<string>();
    for (const v of visitas) {
      const dia = (v.data ?? '').slice(0, 10);
      if (!dia) continue;
      servico.add(dia);
      if (v.viagem_id) viagem.add(dia);
    }
    return { servico, viagem };
  }, [visitas]);

  const hojeIso = iso(hoje);
  const total = visitas.length;

  /**
   * Dia passado nasce RECOLHIDO — quem abre a agenda na quarta quer a quarta, e
   * segunda e terça só ocupam a tela até ele rolar. O clique no cabeçalho abre
   * (ou fecha o que estiver aberto), e essa escolha manual vence a regra.
   */
  const recolhido = (chave: string) => {
    const passou = chave < hojeIso;
    return alternados.has(chave) ? !passou : passou;
  };

  /** As mesmas visitas, no formato que o FullCalendar entende. */
  const eventos = useMemo(
    () =>
      visitas.map((v) => ({
        id: String(v.id),
        // Sem hora, o evento é do dia inteiro — o FullCalendar decide por aqui.
        start: v.hora ? `${v.data}T${v.hora}:00` : v.data,
        allDay: !v.hora,
        title: v.cliente_nome ?? v.titulo ?? 'Serviço',
        extendedProps: v,
      })),
    [visitas],
  );

  // Sem loja no caminho, ou loja que não é da pessoa: a primeira dela.
  if (lojasPagina.length > 0 && !lojaAtual) {
    return <Navigate to={caminhoInstalacoes(lojasPagina[0].codigo)} replace />;
  }

  return (
    /* A lista se lê melhor estreita (linha curta cansa menos); o calendário
       quer largura — sete colunas espremidas em 720px truncam tudo. Isso deixou
       de ser um `style` cravado aqui e virou a largura `leitura` da moldura,
       porque apareceu o segundo uso (esta tela e a irmã de celular). */
    <CustomMolduraPagina
      largura={modo === 'lista' ? 'leitura' : 'padrao'}
      titulo={lojaAtual ? `Instalações — ${lojaAtual.nome_curto || lojaAtual.nome}` : 'Instalações da semana'}
      descricao={
        `${segunda.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} a ` +
        `${domingo.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} · ` +
        (total === 0 ? 'nada marcado' : total === 1 ? '1 serviço' : `${total} serviços`)
      }
      acoes={
        <>
          {/* Calendário x Lista. O calendário mostra a semana como semana; a
              lista é o que se lê melhor em tela estreita. */}
          <div className="btn-group btn-group-sm me-2" role="group">
            <button
              type="button"
              className={`btn ${modo === 'lista' ? 'btn-theme' : 'btn-outline-theme'}`}
              onClick={() => setModo('lista')}
            >
              <i className="bi bi-list-ul me-1"></i>Lista
            </button>
            <button
              type="button"
              className={`btn ${modo === 'calendario' ? 'btn-theme' : 'btn-outline-theme'}`}
              onClick={() => setModo('calendario')}
            >
              <i className="bi bi-calendar-week me-1"></i>Calendário
            </button>
          </div>
          {/* RÓTULO FIXO, estado na cor. Antes o texto mudava junto com o
              estado ("Seg–Sex" ↔ "Seg–Dom") e ninguém sabia se estava lendo o
              que ESTÁ ligado ou o que o clique VAI fazer — ainda mais ao lado de
              Lista/Calendário, onde o botão aceso é o modo atual. Agora o texto
              nomeia a coisa e o aceso diz que ela está ligada, igual aos outros. */}
          <button
            type="button"
            className={`btn btn-sm me-2 ${fimDeSemana ? 'btn-theme' : 'btn-outline-theme'}`}
            onClick={() => setFimDeSemana((v) => !v)}
            title={fimDeSemana ? 'Esconder sábado e domingo' : 'Mostrar sábado e domingo'}
          >
            <i className="bi bi-calendar2-week me-1"></i>Sáb e dom
          </button>
          {semanasAdiante > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-theme me-2"
              onClick={() => setSemanasAdiante((n) => Math.max(0, n - 1))}
              title="Voltar para a semana atual"
            >
              <i className="bi bi-chevron-left"></i> Semana atual
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-theme"
            disabled={!proximaLiberada}
            onClick={() => setSemanasAdiante((n) => n + 1)}
            title={
              proximaLiberada
                ? 'Ver a próxima semana'
                : 'A próxima semana ainda não está liberada'
            }
          >
            Próxima semana <i className="bi bi-chevron-right"></i>
          </button>
        </>
      }
    >

      {/* MINI-CALENDÁRIO. Serve para SE SITUAR — "que semana é essa, onde tem
          serviço" — e não para navegar: o horizonte de uma semana é regra, e
          por isso os dias fora dela ficam desabilitados. Fica acima do conteúdo
          no celular e ao lado no computador. */}
      {/* MINI-CALENDÁRIO — no TOPO e FECHADO por padrão (pedido dele). É
          consulta: quem abre o app quer a lista do dia; o calendário serve a
          quem foi se situar ("que semana é essa, onde tem serviço"). Clicar num
          dia ROLA a tela até ele — é a única navegação que faz sentido aqui,
          já que a semana é fixa. */}
      <div className="card adminuiux-card mb-3 mini-agenda">
        <div
          className="card-body py-2 d-flex align-items-center gap-2"
          role="button"
          onClick={() => setMiniAberto((v) => !v)}
        >
          <i className="bi bi-calendar3 text-secondary"></i>
          <span className="small fw-medium">Calendário do mês</span>
          <span className="small text-secondary d-none d-sm-inline">
            · {diasMarcados.servico.size} dia(s) com serviço nesta semana
          </span>
          <i className={`bi ms-auto ${miniAberto ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </div>
        {miniAberto && (
          <div className="card-body pt-0">
            <div className="d-flex justify-content-center">
              <DatePicker
                inline
                locale="pt-BR"
                /* SEM dia selecionado: este calendário informa, não escolhe. */
                selected={null}
                openToDate={segunda}
                minDate={segunda}
                maxDate={domingo}
                onChange={(d) => {
                  if (!d) return;
                  const alvo = document.getElementById(`dia-${iso(d)}`);
                  alvo?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                dayClassName={(d) => {
                  const k = iso(d);
                  const dentro = k >= iso(segunda) && k <= iso(domingo);
                  return [
                    dentro ? 'semana-atual' : '',
                    diasMarcados.viagem.has(k)
                      ? 'dia-viagem'
                      : diasMarcados.servico.has(k)
                        ? 'dia-servico'
                        : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                }}
              />
            </div>
            <div className="d-flex justify-content-center gap-3 small text-secondary mt-1">
              <span className="d-inline-flex align-items-center gap-1">
                <span className="ponto-legenda ponto-servico" /> serviço
              </span>
              <span className="d-inline-flex align-items-center gap-1">
                <span className="ponto-legenda ponto-viagem" /> viagem
              </span>
            </div>
          </div>
        )}
      </div>

      {carregando ? (
        <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
          <span className="spinner-border spinner-border-sm" /> Carregando…
        </div>
      ) : erro ? (
        <div className="alert alert-warning">{erro}</div>
      ) : total === 0 ? (
        <div className="card adminuiux-card">
          <div className="card-body text-center py-5 text-secondary">
            <i className="bi bi-calendar-check display-6 d-block mb-2"></i>
            Nada marcado para esta semana.
          </div>
        </div>
      ) : modo === 'calendario' ? (
        <div className="card adminuiux-card">
          <div className="card-body">
            {/* O MESMO FullCalendar da tela de Agendamentos, limitado a UMA
                semana e sem nada de editar: `dayGridWeek`, sem `editable`, sem
                drag, sem barra de navegação (a semana é a desta tela e ponto —
                o horizonte é a regra, não uma preferência de quem olha). */}
            <FullCalendar
              plugins={[dayGridPlugin]}
              initialView="dayGridWeek"
              initialDate={iso(segunda)}
              /* Semana começa na SEGUNDA. O padrão do FullCalendar é domingo, e
                 aí a grade ia de dom 09 a sáb 15 enquanto os dados buscados vão
                 de seg 10 a dom 16: sobrava um domingo vazio na frente e sumia
                 o do fim. */
              firstDay={1}
              locale={ptBr}
              height="auto"
              weekends={fimDeSemana}
              headerToolbar={false}
              dayMaxEvents={false}
              events={eventos}
              /* Mesmas classes da tela de Agendamentos: fundo discreto e a cor
                 do tipo na BORDA ESQUERDA. O instalador reconhece o card que a
                 Suany montou. */
              eventClassNames={(arg) => {
                const v = arg.event.extendedProps as Visita;
                return ['casaa-cartao-agendamento', `casaa-cartao-agendamento--${papelDo(v)}`];
              }}
              eventClick={(arg) => setDetalhe(arg.event.extendedProps as Visita)}
              eventContent={(arg) => {
                const v = arg.event.extendedProps as Visita;
                return (
                  <div className="p-1 lh-sm overflow-hidden">
                    {v.hora && <div className="fw-semibold small">{v.hora}</div>}
                    <div className="small text-truncate">
                      {v.cliente_nome ?? v.titulo ?? 'Serviço'}
                    </div>
                    {v.bairro_cidade && (
                      <div className="small text-truncate opacity-75">
                        {v.bairro_cidade}
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </div>
        </div>
      ) : (
        dias
          // Dia vazio não vira cabeçalho órfão — exceto hoje, que a pessoa
          // procura na tela para se situar.
          // Fim de semana escondido NUNCA esconde dia com serviço: card
          // invisível é pior que coluna vazia — se marcaram sábado, ele aparece.
          .filter((d) => {
            const dow = d.data.getDay();
            const fds = dow === 0 || dow === 6;
            if (fds && !fimDeSemana && d.itens.length === 0) return false;
            return d.itens.length > 0 || d.chave === hojeIso;
          })
          .map((d) => (
            <div key={d.chave} id={`dia-${d.chave}`} className="mb-3">
              <div
                /* mb-3 (16px), e nao mb-2: medido no DOM havia 16px acima da
                   faixa do dia (a margem do bloco anterior) contra 8px abaixo
                   dela, e o numero ficava colado no primeiro card. Igualado
                   pela distancia maior, a pedido dele. */
                className="d-flex align-items-center gap-2 mb-3"
                role="button"
                onClick={() => alternarDia(d.chave)}
                title={recolhido(d.chave) ? 'Mostrar o dia' : 'Recolher o dia'}
              >
                <span
                  className={`dia-numero ${
                    d.chave === hojeIso
                      ? 'bg-theme-1 text-white'
                      : 'bg-theme-1-subtle text-theme-1'
                  }`}
                >
                  {d.data.toLocaleDateString('pt-BR', { day: '2-digit' })}
                </span>
                <span className="fw-medium text-capitalize">
                  {d.data.toLocaleDateString('pt-BR', { weekday: 'long' })}
                </span>
                {d.chave === hojeIso && (
                  <span className="badge text-bg-light border">hoje</span>
                )}
                {/* A viagem é do DIA, não do card: saber que a terça inteira é
                    uma ida a Trancoso muda como ele se organiza. */}
                {(() => {
                  const viagemId = d.itens.find((v) => v.viagem_id)?.viagem_id;
                  const nome = nomeViagem(viagemId);
                  return nome ? (
                    <span
                      className="badge rounded-pill bg-theme-1 d-inline-flex align-items-center gap-1"
                      /* Nome curto no chip, roteiro inteiro no toque longo /
                         mouse — o card do celular não comporta o itinerário. */
                      title={nomeViagemCompleto(viagemId)}
                    >
                      <i className="bi bi-signpost-2"></i>
                      {nome}
                    </span>
                  ) : null;
                })()}
                <span className="text-secondary small ms-auto">
                  {d.itens.length === 0
                    ? 'sem serviço'
                    : d.itens.length === 1
                      ? '1 serviço'
                      : `${d.itens.length} serviços`}
                </span>
                {d.itens.length > 0 && (
                  <i
                    className={`bi ${
                      recolhido(d.chave) ? 'bi-chevron-down' : 'bi-chevron-up'
                    } text-secondary`}
                  ></i>
                )}
              </div>

              {!recolhido(d.chave) &&
                d.itens.map((v) => (
                <div
                  key={v.id}
                  className={`card mb-2 casaa-cartao-agendamento casaa-cartao-agendamento--${papelDo(v)}`}
                  role="button"
                  onClick={() => setDetalhe(v)}
                >
                  <div className="card-body py-3">
                    <div className="d-flex align-items-start gap-2">
                      <div className="flex-grow-1 lh-sm">
                        {/* Hora primeiro quando existe: é o que muda a ordem do
                            dia dele. Sem hora é o normal na operação. */}
                        <div className="d-flex align-items-center gap-2 mb-1">
                          {/* `v.hora` direto: `data` é só 'YYYY-MM-DD', e montar
                              um Date com ela dava meia-noite UTC — 21:00 em
                              Brasília, igual em todos os cards. */}
                          {v.hora && <span className="fw-semibold">{v.hora}</span>}
                          {v.os_codigo ? (
                            <span className="small text-secondary">
                              {v.os_codigo}
                              {v.os_tipo && ABREV_OS[v.os_tipo] && (
                                <span
                                  className="fw-semibold ms-1"
                                  style={{ color: `var(--casaa-${papelDo(v)})` }}
                                >
                                  | {ABREV_OS[v.os_tipo]}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="badge text-bg-light border">
                              {ROTULO_TIPO[v.tipo] ?? v.tipo}
                            </span>
                          )}
                        </div>
                        <div className="fw-medium">
                          {v.cliente_nome ?? v.titulo ?? 'Serviço'}
                        </div>
                        {v.endereco_resumo && (
                          <div className="text-secondary small mt-1">
                            <i className="bi bi-geo-alt me-1"></i>
                            {v.endereco_resumo}
                          </div>
                        )}
                        {/* A pauta é o "o que fazer nesta ida" — é a razão de
                            ele abrir a tela. */}
                        {v.pauta && (
                          <div className="small mt-2">
                            <i className="bi bi-card-text me-1 text-secondary"></i>
                            {v.pauta}
                          </div>
                        )}
                      </div>
                      {v.endereco_resumo && (
                        // Abre o mapa no app nativo do celular. É a única ação
                        // desta tela: ele não edita nada aqui.
                        <a
                          className="btn btn-sm btn-outline-theme flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            v.endereco_resumo,
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Abrir no mapa"
                        >
                          <i className="bi bi-map"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                ))}
            </div>
          ))
      )}

      {/* DETALHE — só leitura. O instalador não edita nada; se algo estiver
          errado, quem corrige é quem marcou.

          `fullscreen="sm-down"`: no celular o modal ocupa a tela inteira e vira
          uma página na prática, sem duplicar tela. No computador segue modal. */}
      <Modal
        className="casaa-modal"
        show={detalhe !== null}
        onHide={() => setDetalhe(null)}
        centered
        fullscreen="sm-down"
      >
        {detalhe && (
          <>
            <Modal.Header closeButton>
              <div>
                <div className="small text-secondary">
                  {detalhe.os_codigo ?? ROTULO_TIPO[detalhe.tipo] ?? detalhe.tipo}
                  {detalhe.os_tipo && ABREV_OS[detalhe.os_tipo] && (
                    <span
                      className="fw-semibold ms-1"
                      style={{ color: `var(--casaa-${papelDo(detalhe)})` }}
                    >
                      | {ABREV_OS[detalhe.os_tipo]}
                    </span>
                  )}
                </div>
                <Modal.Title as="h6" className="mb-0">
                  {detalhe.cliente_nome ?? detalhe.titulo ?? 'Serviço'}
                </Modal.Title>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-calendar-event text-secondary"></i>
                <span>
                  {new Date(`${detalhe.data}T12:00:00`).toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                  })}
                </span>
                {detalhe.hora && (
                  <span className="badge bg-theme-1-subtle text-theme-1">
                    {detalhe.hora}
                  </span>
                )}
              </div>

              {detalhe.endereco_resumo && (
                <div className="d-flex align-items-start gap-2 mb-3">
                  <i className="bi bi-geo-alt text-secondary mt-1"></i>
                  <div className="flex-grow-1">{detalhe.endereco_resumo}</div>
                </div>
              )}

              {nomeViagem(detalhe.viagem_id) && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-signpost-2 text-secondary"></i>
                  <span>
                    {/* No detalhe há largura: aqui vale o roteiro por extenso,
                        não o apelido — é onde ele confere para onde vai. */}
                    Faz parte da viagem{' '}
                    <strong>
                      {nomeViagemCompleto(detalhe.viagem_id) ??
                        nomeViagem(detalhe.viagem_id)}
                    </strong>
                  </span>
                </div>
              )}

              {/* A pauta é a razão de ele abrir o card: o que fazer nesta ida. */}
              <div className="card adminuiux-card">
                <div className="card-body py-3">
                  <div className="small text-secondary mb-1">
                    <i className="bi bi-card-text me-1"></i>O que fazer
                  </div>
                  {detalhe.pauta ? (
                    <div>{detalhe.pauta}</div>
                  ) : (
                    <div className="text-secondary fst-italic">
                      Nada anotado para esta ida.
                    </div>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              {/* Da agenda para a OS: é lá que o instalador põe a foto e
                  registra a execução. Sem isto ele voltava à lista de OS para
                  procurar (ensaio de 29/10/2026). */}
              {detalhe.os_id && (
                <Link className="btn btn-outline-theme" to={`/ordens-servico/${detalhe.os_id}`}>
                  <i className="bi bi-clipboard-check me-1"></i>Abrir a OS
                </Link>
              )}
              {detalhe.endereco_resumo && (
                <a
                  className="btn btn-theme"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    detalhe.endereco_resumo,
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="bi bi-map me-1"></i>Abrir no mapa
                </a>
              )}
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setDetalhe(null)}
              >
                Fechar
              </button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </CustomMolduraPagina>
  );
}
